import { Nullable, Scenario } from '@xplan-pension-switching/common';
import { ScenarioTableRow } from '../../components/Scenario/ScenarioTable/ScenarioTableColumns/ScenarioTableColumns';
import { useNavigate, useSearchParams } from 'react-router';
import { useCallback } from 'react';
import { IressButton, IressIcon, IressInline, useModal } from '@iress-oss/ids-components';
import { dateTimeFormatter } from '../../components/Scenario/ScenarioTable/DateTimeFormatter';
import { usePageSearchParams } from '../UsePageSearchParams/UsePageSearchParams';
import { useScenarioSlideout } from '../UseScenarioSlideout/UseScenarioSlideout';

export const useScenarioTableData = (
    scenarios: Scenario[]
): { rows: ScenarioTableRow[]; selectedScenario: Nullable<Scenario> } => {
    const { entityId } = usePageSearchParams();
    const navigate = useNavigate();
    const { showModal } = useModal();
    const [urlParams, setUrlParams] = useSearchParams();
    const { showScenarioSlideout, scenario } = useScenarioSlideout();

    const handleScenarioEdit = useCallback(
        (id: number, ownerId: number) => {
            navigate({
                pathname: `/current-plan-projections`,
                search: `?entityid=${entityId}&scenarioId=${id}&ownerId=${ownerId}`,
            });
        },
        [entityId, navigate]
    );

    return {
        rows: scenarios.map(scenario => ({
            ...scenario,
            actionCell: (
                <IressInline gutter={'none'}>
                    <IressButton data-testid={`scenario-lock-button-${scenario.id}`} mode={'tertiary'}>
                        <IressIcon
                            screenreaderText="Lock or unlock scenario"
                            data-testid={`scenario-lock-icon-${scenario.id}`}
                            name={!scenario.isLocked ? 'unlock' : 'lock'}
                        />
                    </IressButton>
                    {!scenario.isLocked ? (
                        <IressButton
                            mode={'tertiary'}
                            onClick={() => {
                                urlParams.set('editScenarioId', scenario.id.toString());
                                setUrlParams(urlParams);
                                showModal('edit-scenario-modal');
                            }}
                        >
                            <IressIcon
                                screenreaderText="Edit scenario name"
                                data-testid={`scenario-edit-icon-${scenario.id}`}
                                name={'edit'}
                            />
                        </IressButton>
                    ) : null}
                </IressInline>
            ),
            scenarioCell: (
                <IressButton
                    mode="link"
                    onClick={() => {
                        handleScenarioEdit(scenario.id, scenario.ownerId);
                    }}
                    disabled={scenario.isLocked}
                >
                    {scenario.scenarioName}
                </IressButton>
            ),
            reportsCell: (
                <IressButton
                    mode="link"
                    data-testid={`view-report--${scenario.id}`}
                    onClick={() => {
                        showScenarioSlideout(scenario);
                    }}
                >
                    {`View (${scenario.reports.length})`}
                </IressButton>
            ),
            reportCount: scenario.reports ? scenario.reports.length : 0,
            createdByCell: dateTimeFormatter(scenario.createdBy, scenario.createdDate),
            lastModifiedCell: dateTimeFormatter(scenario.modifiedBy, scenario.modifiedDate),
        })),
        selectedScenario: scenario,
    };
};
